/**
 * Constitution / Mission / Principles / Strategy / Goals UI contracts.
 *
 * Maps the FastAPI surface of ``services/constitution-service`` into wire-shape
 * Zod schemas reused by chaos-gateway (validation + camelCase translation)
 * and chaos-ui (typed API client + form schemas).
 *
 * The upstream service uses snake_case on the wire — the gateway's
 * ``learning.ts``/``departments.ts`` precedent translates to camelCase, and
 * this Phase 10 surface continues that convention.
 */
import { z } from 'zod';

// ── Severity vocabulary (matches Pydantic enum) ──────────────────────────
export const constitutionSeverities = [
  'critical',
  'high',
  'medium',
  'low',
  'informational',
] as const;
export const constitutionSeveritySchema = z.enum(constitutionSeverities);
export type ConstitutionSeverity = z.infer<typeof constitutionSeveritySchema>;

// ── Proposal workflow status taxonomy ────────────────────────────────────
// Upstream returns one of these on ProposalAck (`pending_founder_approval`,
// `auto_activated`, `queued_offline`). The UI extends with workflow-stage
// labels emitted by Temporal during polling.
export const proposalWorkflowStatuses = [
  'pending_founder_approval',
  'auto_activated',
  'queued_offline',
  'in_review',
  'approved',
  'rejected',
  'applied',
  'failed',
] as const;
export const proposalWorkflowStatusSchema = z.enum(proposalWorkflowStatuses);
export type ProposalWorkflowStatus = z.infer<typeof proposalWorkflowStatusSchema>;

// Proposal-kind discriminator (mirrors `ChangeProposalKind` upstream).
export const proposalKinds = [
  'constitution_rule',
  'mission',
  'principle',
  'strategy',
  'goal',
] as const;
export const proposalKindSchema = z.enum(proposalKinds);
export type ProposalKind = z.infer<typeof proposalKindSchema>;

// ── Constitution rule (gateway camelCase shape) ──────────────────────────
export const constitutionRuleSchema = z.object({
  id: z.string().uuid(),
  constitutionId: z.string().uuid(),
  ruleKey: z.string(),
  statement: z.string(),
  rationale: z.string().nullable(),
  scope: z.string().nullable(),
  severity: constitutionSeveritySchema,
  exampleJsonb: z.unknown().nullable(),
  antiExampleJsonb: z.unknown().nullable(),
  inputRailPattern: z.string().nullable(),
  outputRailPattern: z.string().nullable(),
  createdAt: z.string(),
  /** Derived: true when this rule is part of the immutable-set (severity === 'critical'). */
  immutable: z.boolean(),
});
export type ConstitutionRule = z.infer<typeof constitutionRuleSchema>;

export const constitutionDocSchema = z.object({
  id: z.string().uuid(),
  version: z.number().int().positive(),
  contentMd: z.string(),
  scope: z.string(),
  scopeId: z.string().uuid().nullable(),
  effectiveFrom: z.string(),
  supersededBy: z.string().uuid().nullable(),
  approvedBy: z.string().uuid().nullable(),
  approvedAt: z.string().nullable(),
  changeSummary: z.string().nullable(),
  colangCompiled: z.string().nullable(),
  createdAt: z.string(),
  rules: z.array(constitutionRuleSchema),
});
export type ConstitutionDoc = z.infer<typeof constitutionDocSchema>;

// Wrapper used by /api/constitution/rules — includes the active doc version
// so callers can show "Constitution v3" next to each rule without an extra
// fetch.
export const constitutionRulesListSchema = z.object({
  rules: z.array(constitutionRuleSchema),
  activeDocVersion: z.number().int().positive().nullable(),
  activeDocId: z.string().uuid().nullable(),
});
export type ConstitutionRulesList = z.infer<typeof constitutionRulesListSchema>;

// ── Constitution rule proposal request ───────────────────────────────────
export const constitutionRuleProposalSchema = z.object({
  ruleKey: z.string().min(1).max(120),
  statement: z.string().min(1).max(4000),
  rationale: z.string().max(4000).nullable().optional(),
  severity: constitutionSeveritySchema.default('high'),
  scope: z.string().default('global'),
  scopeId: z.string().uuid().nullable().optional(),
  exampleJsonb: z.unknown().nullable().optional(),
  antiExampleJsonb: z.unknown().nullable().optional(),
  inputRailPattern: z.string().nullable().optional(),
  outputRailPattern: z.string().nullable().optional(),
  changeSummary: z.string().max(500).nullable().optional(),
});
export type ConstitutionRuleProposal = z.infer<typeof constitutionRuleProposalSchema>;

// ── Mission ──────────────────────────────────────────────────────────────
export const missionSchema = z.object({
  id: z.string().uuid(),
  version: z.number().int().positive(),
  statement: z.string(),
  rationale: z.string().nullable(),
  scope: z.string(),
  scopeId: z.string().uuid().nullable(),
  effectiveFrom: z.string(),
  supersededBy: z.string().uuid().nullable(),
  founderApprovedAt: z.string().nullable(),
  founderApprovedBy: z.string().uuid().nullable(),
  createdAt: z.string(),
});
export type Mission = z.infer<typeof missionSchema>;

export const missionProposalSchema = z.object({
  statement: z.string().min(1).max(8000),
  rationale: z.string().max(4000).nullable().optional(),
  scope: z.string().default('global'),
  scopeId: z.string().uuid().nullable().optional(),
});
export type MissionProposal = z.infer<typeof missionProposalSchema>;

// ── Principles ───────────────────────────────────────────────────────────
// `applies_to` is a free-form list of department keys + autonomy levels
// ('HITL', 'HOTL', 'HOOL').
export const principleSchema = z.object({
  id: z.string().uuid(),
  version: z.number().int().positive(),
  principleKey: z.string(),
  statement: z.string(),
  appliesTo: z.array(z.string()),
  exampleJsonb: z.unknown().nullable(),
  antiExampleJsonb: z.unknown().nullable(),
  scope: z.string(),
  scopeId: z.string().uuid().nullable(),
  effectiveFrom: z.string(),
  supersededBy: z.string().uuid().nullable(),
  createdAt: z.string(),
});
export type Principle = z.infer<typeof principleSchema>;

export const principlesListSchema = z.object({
  principles: z.array(principleSchema),
});
export type PrinciplesList = z.infer<typeof principlesListSchema>;

export const principleProposalSchema = z.object({
  principleKey: z.string().min(1).max(120),
  statement: z.string().min(1).max(4000),
  appliesTo: z.array(z.string()).default([]),
  scope: z.string().default('global'),
  scopeId: z.string().uuid().nullable().optional(),
  example: z.unknown().nullable().optional(),
  antiExample: z.unknown().nullable().optional(),
});
export type PrincipleProposal = z.infer<typeof principleProposalSchema>;

// ── Strategy ─────────────────────────────────────────────────────────────
// Matches the DB CHECK in migration 008_strategies_goals.py:
//   status IN ('draft','approved','active','archived','retired')
export const strategyStatuses = ['draft', 'approved', 'active', 'archived', 'retired'] as const;
export const strategyStatusSchema = z.enum(strategyStatuses);
export type StrategyStatus = z.infer<typeof strategyStatusSchema>;

export const strategySchema = z.object({
  id: z.string().uuid(),
  period: z.string(),
  title: z.string(),
  bodyMd: z.string(),
  parentStrategyId: z.string().uuid().nullable(),
  ownerHumanId: z.string().uuid().nullable(),
  scope: z.string(),
  scopeId: z.string().uuid().nullable(),
  status: strategyStatusSchema,
  founderApprovedAt: z.string().nullable(),
  founderApprovedBy: z.string().uuid().nullable(),
  metadataJsonb: z.record(z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Strategy = z.infer<typeof strategySchema>;

export const strategiesListSchema = z.object({
  strategies: z.array(strategySchema),
});
export type StrategiesList = z.infer<typeof strategiesListSchema>;

export const strategyProposalSchema = z.object({
  period: z.string().min(1).max(40),
  title: z.string().min(1).max(200),
  bodyMd: z.string().min(1).max(50000),
  parentStrategyId: z.string().uuid().nullable().optional(),
  ownerHumanId: z.string().uuid().nullable().optional(),
  scope: z.string().default('global'),
  scopeId: z.string().uuid().nullable().optional(),
  metadata: z.record(z.unknown()).default({}),
});
export type StrategyProposal = z.infer<typeof strategyProposalSchema>;

// ── Goals ────────────────────────────────────────────────────────────────
// Matches the DB CHECK in migration 008_strategies_goals.py:
//   status IN ('open','in_progress','blocked','done','dropped')
export const goalStatuses = ['open', 'in_progress', 'blocked', 'done', 'dropped'] as const;
export const goalStatusSchema = z.enum(goalStatuses);
export type GoalStatus = z.infer<typeof goalStatusSchema>;

export const goalSchema = z.object({
  id: z.string().uuid(),
  strategyId: z.string().uuid().nullable(),
  parentGoalId: z.string().uuid().nullable(),
  goalKey: z.string(),
  statement: z.string(),
  kpi: z.record(z.unknown()),
  targetValue: z.number().nullable(),
  currentValue: z.number().nullable(),
  unit: z.string().nullable(),
  deadline: z.string().nullable(),
  linkedProjectIds: z.array(z.string().uuid()),
  linkedBoardIds: z.array(z.string().uuid()),
  ownerHumanId: z.string().uuid().nullable(),
  status: goalStatusSchema,
  metadataJsonb: z.record(z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Rolled-up progress (0..1) derived from current/target + linked cards. */
  progress: z.number().min(0).max(1).nullable(),
  /** Direct children count (computed by the gateway). */
  childCount: z.number().int().nonnegative(),
});
export type Goal = z.infer<typeof goalSchema>;

export const goalsListSchema = z.object({
  goals: z.array(goalSchema),
});
export type GoalsList = z.infer<typeof goalsListSchema>;

export const goalProposalSchema = z.object({
  strategyId: z.string().uuid().nullable().optional(),
  parentGoalId: z.string().uuid().nullable().optional(),
  goalKey: z.string().min(1).max(120),
  statement: z.string().min(1).max(2000),
  kpi: z.record(z.unknown()).default({}),
  targetValue: z.number().nullable().optional(),
  unit: z.string().nullable().optional(),
  deadline: z.string().nullable().optional(),
  linkedProjectIds: z.array(z.string().uuid()).default([]),
  linkedBoardIds: z.array(z.string().uuid()).default([]),
  ownerHumanId: z.string().uuid().nullable().optional(),
  metadata: z.record(z.unknown()).default({}),
});
export type GoalProposal = z.infer<typeof goalProposalSchema>;

export const goalPatchSchema = z.object({
  statement: z.string().min(1).max(2000).optional(),
  status: goalStatusSchema.optional(),
  parentGoalId: z.string().uuid().nullable().optional(),
  currentValue: z.number().nullable().optional(),
  deadline: z.string().nullable().optional(),
  ownerHumanId: z.string().uuid().nullable().optional(),
  linkedProjectIds: z.array(z.string().uuid()).optional(),
  linkedBoardIds: z.array(z.string().uuid()).optional(),
});
export type GoalPatch = z.infer<typeof goalPatchSchema>;

// ── Proposal ack + listing ──────────────────────────────────────────────
export const proposalAckSchema = z.object({
  proposalId: z.string().uuid(),
  workflowId: z.string().nullable(),
  status: proposalWorkflowStatusSchema,
  message: z.string().nullable(),
});
export type ProposalAck = z.infer<typeof proposalAckSchema>;

/**
 * Proposal entry as returned by /api/constitution/proposals — the gateway
 * stitches together rows from `mission` / `principles` / `strategies` /
 * `constitution` + Temporal workflow status.
 */
export const changeProposalSchema = z.object({
  proposalId: z.string().uuid(),
  kind: proposalKindSchema,
  title: z.string(),
  summary: z.string().nullable(),
  proposerUserId: z.string().uuid().nullable(),
  proposerEmail: z.string().nullable(),
  workflowId: z.string().nullable(),
  status: proposalWorkflowStatusSchema,
  scope: z.string(),
  scopeId: z.string().uuid().nullable(),
  createdAt: z.string(),
  decidedAt: z.string().nullable(),
  decidedByUserId: z.string().uuid().nullable(),
  decisionNotes: z.string().nullable(),
});
export type ChangeProposal = z.infer<typeof changeProposalSchema>;

export const changeProposalsListSchema = z.object({
  proposals: z.array(changeProposalSchema),
});
export type ChangeProposalsList = z.infer<typeof changeProposalsListSchema>;

export const proposalDecisionSchema = z.object({
  notes: z.string().max(2000).optional().default(''),
});
export type ProposalDecision = z.infer<typeof proposalDecisionSchema>;
